<script setup lang="ts">
import './assets/style.scss'
import { computed, nextTick, type PropType, ref, watch } from 'vue'

const emit = defineEmits<{
  /** Серверный поиск: термин ввода пробрасывается наружу для загрузки элементов. */
  'update:search': [term: string]
}>()

const props = defineProps({
  items: {
    type: Array as PropType<any[]>,
    default: () => [],
  },
  /**
   * Минимальное количество символов для запуска фильтрации.
   * По умолчанию — 3, чтобы сохранить текущее поведение.
   */
  minSearchLength: {
    type: Number,
    default: 3,
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  hideDetails: {
    type: Boolean,
    default: false,
  },
  itemValue: {
    type: String,
    default: 'value',
  },
  itemTitle: {
    type: String,
    default: 'title',
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  rules: {
    type: Array as PropType<any>,
    default: () => [],
  },
  short: {
    type: Boolean,
    default: false,
  },
  /**
   * Значения (по itemValue), которые нельзя выкидывать при поиске.
   * Используется, чтобы опция «пусто» всегда оставалась первой в списке.
   */
  preserveValues: {
    type: Array as PropType<unknown[]>,
    default: () => [],
  },
  /**
   * Серверный режим: элементы фильтруются на бэкенде, а не локально.
   * VSelectSearch не фильтрует список сам, а пробрасывает термин поиска
   * через update:search — родитель сам подгружает нужные элементы.
   */
  serverSide: {
    type: Boolean,
    default: false,
  },
})

const model = defineModel<any>({ required: true })

const search = ref('')

/** В серверном режиме термин ввода уходит родителю для подгрузки элементов. */
watch(search, (term) => {
  if (props.serverSide) emit('update:search', term)
})

const selectedSet = computed(() => {
  const values = props.multiple
    ? (model.value || []).map((v: any) => (typeof v === 'object' ? v[props.itemValue] : v))
    : [model.value]
  return new Set(values.filter((v: any) => v !== undefined && v !== null))
})

const listItems = computed(() => {
  if (props.serverSide) return props.items

  if (search.value.length < props.minSearchLength) return props.items

  const query = search.value.toLowerCase()
  const preserve = new Set(props.preserveValues.map((v) => String(v)))
  return props.items.filter((item: any) => {
    const title = String(item[props.itemTitle] ?? '').toLowerCase()
    const value = item[props.itemValue]
    return title.includes(query) || selectedSet.value.has(value) || preserve.has(String(value))
  })
})

/**
 * Функция обработки клика
 * @param event
 */
const clickHandler = (event: MouseEvent) => {
  ;(event.target as HTMLInputElement).focus()
}

/**
 * Очищаем поиск при закрытии меню, а не на focusout инпута,
 * чтобы клик по отфильтрованному элементу не прерывался
 * перерисовкой списка.
 *
 * Откладываем сброс на nextTick, чтобы VSelect успел завершить
 * обработку выбора до того, как список items изменится.
 */
const menuUpdateHandler = async (isOpen: boolean) => {
  if (!isOpen) {
    await nextTick()
    search.value = ''
  }
}
</script>

<template>
  <VSelect
    v-model="model"
    class="v-select-search"
    :class="{ 'v-select-search--loading': loading }"
    :aria-busy="loading"
    :items="listItems"
    :label="label"
    :placeholder="placeholder"
    :hide-details="hideDetails"
    :item-value="itemValue"
    :item-title="itemTitle"
    :multiple="multiple"
    :disabled="disabled"
    :rules="rules"
    @update:menu="menuUpdateHandler"
  >
    <template #prepend-item>
      <div class="v-select-search__search">
        <VTextField
          hide-details
          v-model="search"
          :placeholder="$t('itf.search')"
          @click.stop="clickHandler"
          @keydown.stop
          @keyup.stop
          @input.stop
          @focusout.stop
        />
      </div>
    </template>

    <template v-if="$slots.item" #item="{ item, index, props }">
      <slot
        name="item"
        :item="item as any"
        :index="index as number"
        :props="props as Record<string, unknown>"
      />
    </template>

    <template v-if="short" v-slot:selection="{ item, index }">
      <span v-if="index < 2">{{ item.title }},</span>

      <span v-if="index === 2" class="text-grey text-caption align-self-center">
        ( +{{ $t('itf.more', { num: model.length - 2 }) }})
      </span>
    </template>
  </VSelect>
</template>
